<template>
	<div
		class="text-gray-700 focus-within:text-orange-500"
	>
		<div class="flex justify-between flex-wrap">
			<label class="flex font-medium" :for="inputId" v-if="!noLabel">
				{{ __(label) }}

				<ui-tooltip
					v-if="tooltipText"
					class="flex text-green-400 whitespace-normal"
					:icon-stroke-width="2"
					icon-class="ml-1 w-4 h-4"
					:text="__(tooltipText)"
				>
					<ui-icon
						class="sm:mr-2 flex-shrink-0 h-6 w-6 md:h-5 md:w-5"
						name="help-circle"
						stroke-width="2"
					/>
				</ui-tooltip>
			</label>
			<span class="text-gray-500" v-if="!noLabel && !required && !disabled && !markInvalid && !inValid">optional</span>
		</div>
		<div class="mt-1 relative">
			<ui-input
				v-model="val"
				:type="type"
				:id="inputId"
				:autocomplete="autocomplete"
				:inputId="inputId"
				:disabled="disabled"
				:markInvalid="markInputInvalid"
				:dp="dp"
				:maxValue="maxValue"
				:showZeroValue="showZeroValue"
				:field="field"
				:autoReplace="autoReplace"
				@focus.native="onFocus"
			/>
			<div
				class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
				v-if="markInvalid"
			>
				<ui-icon class="h-5 w-5 text-red-600" name="alert-triangle" stroke-width="1.5" />
			</div>
			<div
				v-else-if="suffix"
				class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
			>
				<span :class="['text-gray-500', inValid ? 'text-red-600' : '']">
					{{ suffix }}
				</span>
			</div>
		</div>
		<span class="text-red-600" v-if="inValid">{{ firstValidationError }}</span>
		<span class="text-red-600" v-else-if="markInvalid">{{ validationMessage }}</span>
	</div>
</template>

<script>
export default {
	props: {
		value: {
			required: true,
		},
		form: {
			type: Object,
			required: true,
		},
		field: {
			type: String,
			required: true,
		},
		label: {
			type: String,
			required: false,
		},
		labelIsBold: {
			type: Boolean,
			required: false,
		},
		required: {
			type: Boolean,
			default: false,
		},
		type: {
			type: String,
			default: "text",
		},
		autocomplete: {
			type: String,
			default: "on",
		},
		autoReplace: {
			required: false,
			default: true,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		suffix: {
			type: String,
			required: false,
		},
		noLabel: {
			type: Boolean,
			default: false,
		},
		dp: {
			type: Number,
			default: null,
		},
		maxValue: {
			type: Number,
			default: null,
			required: false,
		},
		showAsInvalid: {
			type: Boolean,
			default: false,
			required: false,
		},
		showZeroValue: {
			type: Boolean,
			default: false,
			required: false,
		},
		tooltipText: {
			type: String,
			default: "",
		},
	},
	data() {
		return {
			val: this.value,
			originalVal: this.value,
			validationErrors: null,
		};
	},
	mounted() {
		this.$bus.$off("form:invalid", this.setValidationErrors);
		this.$bus.$on("form:invalid", this.setValidationErrors);
	},
	computed: {
		inValid() {
			return this.validationErrors?.length > 0;
		},
		firstValidationError() {
			return this.validationErrors[0] ?? "";
		},
		markInvalid() {
			return this.form.hasErrors && this.form.errors[this.field] != null;
		},
		markInputInvalid() {
			return this.markInvalid || this.showAsInvalid;
		},
		validationMessage() {
			return this.form.errors[this.field] || "";
		},
		inputId() {
			return "input-" + this._uid;
		},
	},
	watch: {
		/**
		 * Synchronisiert das interne `val` mit dem von außen gebundenen `value`-Prop.
		 *
		 * Hintergrund: `val` wird in `data()` nur EINMAL initial aus `value` gesetzt. Ohne
		 * diesen Watcher kämen spätere Prop-Änderungen des Parents (asynchron nachgeladene
		 * Formulardaten, Reset, programmatisches Setzen) nicht mehr im Feld an.
		 *
		 * `_propSync` markiert, dass die dadurch ausgelöste `val`-Änderung aus dem Prop stammt
		 * und NICHT aus einer Nutzereingabe — damit der `val`-Watcher unten kein `change`-Event
		 * feuert (ein reines Prop-Update darf nicht als Nutzer-Änderung durchgehen).
		 *
		 * Gilt via Mixin (`mixins: [UiInputGroup]`) auch für `SelectGroup` — dort ist keine
		 * eigene Watcher-Logik nötig.
		 *
		 * @param {*} v Neuer Wert des `value`-Props
		 */
		value(v) {
			if (v !== this.val) {
				this._propSync = true;
				this.val = v;
			}
		},
		/**
		 * Meldet Änderungen an `val` nach außen: `input` immer (v-model-Vertrag), `change`
		 * nur bei echter Nutzereingabe. Bei einem Prop-Sync (siehe `_propSync`) wird `change`
		 * unterdrückt; anschließend wird das Flag wieder zurückgesetzt.
		 *
		 * @param {*} v Neuer Wert von `val`
		 */
		val(v) {
			this.$emit("input", v);

			if (!this._propSync) {
				this.$emit("change");
			}

			this._propSync = false;
		},
	},
	methods: {
		onFocus() {
			// Axios-Fehler (bus-basiert, z.B. SaleIntentions/Form)
			this.validationErrors = null;

			// Inertia-Fehler (form.errors) — entfällt wenn Inertia entfernt wird.
			// Guard: bei Nicht-Inertia-Forms (Plain-Object/Stub) fehlt clearErrors.
			if (this.form && this.field && typeof this.form.clearErrors === "function") {
				this.form.clearErrors(this.field);
			}
		},
		setValidationErrors(event) {
			this.validationErrors = event[this.field];
		},
		validationEventListener() {
			this.$bus.$emit("is-mark-invalid", this.field, this.markInvalid);
		},
	},
	created() {
		this.$bus.$on("poll-is-mark-invalid", this.validationEventListener);
	},
	beforeDestroy() {
		this.$bus.$off("form:invalid", this.setValidationErrors);
		this.$bus.$off("poll-is-mark-invalid", this.validationEventListener);
	},
};
</script>
